<template>
  <el-dialog
    class="dialog-wrapper"
    visible
    :show-close="false"
    @close="abortCreate"
  >
    <ContentHeader
      slot="title"
      :title="
        isModalEdit
          ? $t('sectorsAssociation.create.editSector')
          : $t('sectorsAssociation.create.createSector')
      "
    />
    <ValidationObserver
      ref="recipientForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('sectorsAssociation.create.sector')"
            >
              <el-select
                v-model="formData.sector"
                placeholder="Select"
              >
                <el-option
                  v-for="item in sectors"
                  :key="item.value"
                  :label="$t(item.label)"
                  :value="item.value"
                >
                </el-option>
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('sectorsAssociation.create.laboratory')"
            >
              <el-select
                v-model="formData.department"
                placeholder=""
              >
                <el-option
                  v-for="laboratory in laboratories"
                  :key="laboratory"
                  :value="laboratory"
                  :label="$t(`constants.${laboratory}`)"
                />
              </el-select>
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('sectorsAssociation.create.location')"
            >
              <el-autocomplete
                v-model="locationName"
                style="width: 100%"
                :fetch-suggestions="locationsList"
                :trigger-on-focus="false"
                value-key="label"
                clearable
                placeholder=""
                @select="
                  (item) => {
                    formData.locationId = item.value;
                  }
                "
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </FormItem>
          </el-col>
        </el-row>

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortCreate"
          >
            {{ $t('sectorsAssociation.create.abort') }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(createUpdateSector)"
          >
            {{ $t('sectorsAssociation.create.save') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { sectorsAssociationApi, locationsManagementApi } from '@/api';
  import { sectorsList, laboratories } from '@/config/constants';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';
  import { enumOptions } from '@/config/enumOptions';

  const defaultData = {
    sector: '',
    department: '',
    locationId: '',
  };

  export default {
    name: 'CreateEditLocation',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
      AutocompleteItem,
    },
    props: {
      initialData: { type: Object, default: () => defaultData },
      isModalEdit: { type: Boolean, default: false },
    },
    data() {
      return {
        formData: { ...defaultData },
        laboratories,
        locationName: null,
      };
    },
    computed: {
      sectors() {
        return enumOptions(sectorsList, 'enums.sectors', this.formData.sector);
      },
    },
    async created() {
      try {
        if (this.isModalEdit) {
          const { sector, department, locationId } = this.initialData;
          this.formData = {
            ...defaultData,
            sector,
            department,
            locationId,
          };
          if (locationId) {
            const response = await locationsManagementApi.getLocation(
              locationId
            );
            this.locationName = response.data.name;
          }
        }
      } catch (error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: error.response.data.error.message,
        });
      }
    },
    methods: {
      async createUpdateSector() {
        try {
          if (this.isModalEdit) {
            await sectorsAssociationApi.patch(
              this.initialData.id,
              this.formData
            );
          } else {
            await sectorsAssociationApi.create(this.formData);
          }
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('sectorsAssociation.create.success'),
          });
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      abortCreate() {
        this.$emit('dialogClose', { success: false });
      },
      async locationsList(value, cb) {
        if (value && value.length >= 3) {
          const response = await locationsManagementApi.autocompleteFetch(
            'name',
            value,
            false
          );
          const locations = response.data.map((c) => ({
            label: c.name,
            value: c.id,
          }));
          cb(locations);
        } else {
          cb([]);
        }
      },
    },
  };
</script>
